import { useStoreContactCallLog } from '@gozoki/api'
import type { StoreContactService } from '@gozoki/api-types'
import { cn } from '@gozoki/ui/lib/utils'
import { useEffect, useState } from 'react'
import { ModalCloseButton } from './ui/ModalCloseButton'
import { findService } from './contact-directory'

/**
 * Modale de confirmation d'appel (Figma 10263:2300), ouverte depuis un CTA de la carte CONTACT.
 * Même overlay que `CaDetailModal` : posée sur `document.body` via `fixed`, donc hors du
 * `transform: scale()` du dashboard. L'appel est passé via lien `tel:` ET journalisé (audit).
 */
export function ContactModal({
  service,
  storeLabel,
  onClose,
}: {
  service: StoreContactService
  storeLabel?: string
  onClose: () => void
}) {
  const definition = findService(service)
  const { theme, contacts, label, subtitle, Icon } = definition
  const [selected, setSelected] = useState(0)
  const { mutate } = useStoreContactCallLog()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const call = () => {
    const contact = contacts[selected]
    if (!contact) return
    // On journalise l'intention ; l'échec réseau ne doit pas bloquer l'appel.
    mutate({ service, contactName: contact.role, phoneNumber: contact.phone })
    window.location.href = `tel:${contact.phone.replace(/\s/g, '')}`
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(7,44,26,0.25)] p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label={`Contact ${label}`}
      onClick={onClose}
    >
      <div
        className="flex flex-col gap-[20px] rounded-[20px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-[12px]">
          <h2 className="font-sans text-[22px] leading-[24px] font-semibold text-[#181D27] uppercase">
            Contact{storeLabel ? ` ${storeLabel}` : ''}
          </h2>
          <ModalCloseButton onClose={onClose} />
        </div>

        <div className="flex w-[480px] flex-col items-center gap-[18px] py-[8px]">
          {/* Carte service */}
          <div
            className="flex w-full items-center gap-[16px] rounded-[16px] border p-[19px]"
            style={{ backgroundColor: theme.cardBg, borderColor: theme.cardBorder }}
          >
            <div
              className="flex size-[64px] shrink-0 items-center justify-center rounded-[32px] border bg-white"
              style={{ borderColor: theme.cardBorder, color: theme.accent }}
            >
              <Icon className="size-[34px]" />
            </div>
            <div className="flex flex-col gap-[2px]">
              <span
                className="font-sans text-[24px] font-extrabold uppercase"
                style={{ color: theme.accent }}
              >
                {label}
              </span>
              <span className="font-sans text-[13px] text-[#6B6B6B]">{subtitle}</span>
            </div>
          </div>

          <p className="text-center font-sans text-[14px] text-[#6B6B6B]">
            L'appel sera passé depuis la tablette de la boutique
            <br />
            et tracé dans l'historique.
          </p>

          {/* Liste radio des contacts */}
          <div className="flex w-full flex-col gap-[8px]">
            {contacts.map((contact, index) => {
              const active = index === selected
              return (
                <button
                  key={contact.role}
                  type="button"
                  onClick={() => setSelected(index)}
                  aria-pressed={active}
                  className={cn(
                    'flex w-full items-center gap-[12px] rounded-[14px] border px-[17px] py-[13px] text-left',
                    active ? 'border' : 'border-[#D7D7D7] bg-white',
                  )}
                  style={
                    active
                      ? { backgroundColor: theme.cardBg, borderColor: theme.cardBorder }
                      : undefined
                  }
                >
                  <span
                    className="flex size-[18px] shrink-0 items-center justify-center rounded-[9px] border"
                    style={{
                      backgroundColor: active ? theme.accent : 'transparent',
                      borderColor: active ? theme.accent : '#D7D7D7',
                    }}
                  >
                    {active ? (
                      <span className="size-[8px] rounded-full bg-white" aria-hidden />
                    ) : null}
                  </span>
                  <span className="flex flex-col gap-px">
                    <span className="font-sans text-[13px] font-semibold text-[#072C1A]">
                      {contact.role}
                    </span>
                    <span className="font-sans text-[15px] font-bold tracking-[0.5px] text-[#072C1A]">
                      {contact.phone}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>

          {/* Actions */}
          <div className="flex w-full gap-[12px]">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 cursor-pointer rounded-full border border-[#D7D7D7] bg-white px-[25px] py-[13px] text-center font-sans text-[14px] font-semibold text-[#072C1A]"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={call}
              className="flex-1 cursor-pointer rounded-full px-[24px] py-[12px] text-center font-sans text-[14px] font-semibold"
              style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
            >
              Appeler {label}
            </button>
          </div>

          <span className="font-sans text-[11px] text-[#8A887F]">
            Code couleur par service : Supply vert · SAV orange · IT bleu · Urgence rouge
          </span>
        </div>
      </div>
    </div>
  )
}
